import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { Loader2, CheckCircle2, Eye, EyeOff, Zap, AlertTriangle, PartyPopper } from "lucide-react";
import { toast } from "sonner";

export default function WelcomePage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const nav = useNavigate();
  const [form, setForm] = useState({ new_password: "", confirm: "" });
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) toast.error("Missing setup token");
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (form.new_password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    if (form.new_password !== form.confirm) {
      toast.error("Passwords don't match");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: form.new_password });
      setDone(true);
      toast.success("You're all set — sign in to get started");
      setTimeout(() => nav("/login"), 1800);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-brand-yellow p-4">
      <div className="brutal bg-white max-w-md w-full p-8">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-11 h-11 bg-brand-orange border-2 border-black rounded-xl flex items-center justify-center shadow-brutal">
            <Zap className="w-5 h-5 text-white" strokeWidth={3} />
          </div>
          <div>
            <div className="font-display font-black text-xl leading-none">ReferPro</div>
            <div className="text-[11px] uppercase tracking-widest text-neutral-500 mt-1">Welcome aboard</div>
          </div>
        </div>

        {!token ? (
          <div className="text-center" data-testid="welcome-no-token">
            <div className="w-14 h-14 bg-red-200 border-2 border-black rounded-2xl flex items-center justify-center shadow-brutal mb-4 mx-auto">
              <AlertTriangle className="w-7 h-7" strokeWidth={2.4} />
            </div>
            <h2 className="font-display font-black text-2xl">Link looks broken</h2>
            <p className="text-sm text-neutral-700 mt-2">
              We couldn't find a setup token in this link. Ask your admin to resend the welcome email, or reset your password.
            </p>
            <Link to="/forgot-password" className="btn-brutal btn-primary w-full mt-6" data-testid="welcome-go-forgot">
              Reset your password
            </Link>
          </div>
        ) : done ? (
          <div className="text-center" data-testid="welcome-done">
            <div className="w-14 h-14 bg-brand-green border-2 border-black rounded-2xl flex items-center justify-center shadow-brutal mb-4 mx-auto">
              <CheckCircle2 className="w-7 h-7" strokeWidth={2.4} />
            </div>
            <h2 className="font-display font-black text-2xl">You're in! 🎉</h2>
            <p className="text-sm text-neutral-700 mt-2">Taking you to sign in…</p>
          </div>
        ) : (
          <>
            <div className="inline-flex items-center gap-2 px-2.5 py-1 bg-brand-green border-2 border-black rounded-md mb-3">
              <PartyPopper className="w-3.5 h-3.5" strokeWidth={2.5} />
              <span className="text-[10px] font-bold uppercase tracking-widest">First-time setup</span>
            </div>
            <h1 className="font-display font-black text-3xl sm:text-4xl tracking-tight">Welcome to ReferPro.</h1>
            <p className="text-neutral-600 mt-2 text-sm">
              Pick a password to activate your account. You'll use this every time you sign in. Min 8 characters — make it memorable but hard to guess.
            </p>

            <form onSubmit={submit} className="mt-6 space-y-4">
              <div>
                <label className="block text-xs font-bold uppercase tracking-widest mb-2">Choose a password</label>
                <div className="relative">
                  <input
                    type={show ? "text" : "password"}
                    value={form.new_password}
                    onChange={(e) => setForm({ ...form, new_password: e.target.value })}
                    className="input-brutal pr-12"
                    minLength={8}
                    required
                    autoFocus
                    data-testid="welcome-new"
                  />
                  <button
                    type="button"
                    onClick={() => setShow((s) => !s)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-500 hover:text-black"
                    aria-label="Toggle visibility"
                  >
                    {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-widest mb-2">Confirm</label>
                <input
                  type={show ? "text" : "password"}
                  value={form.confirm}
                  onChange={(e) => setForm({ ...form, confirm: e.target.value })}
                  className="input-brutal"
                  required
                  data-testid="welcome-confirm"
                />
              </div>
              <button type="submit" disabled={busy} className="btn-brutal btn-primary w-full py-3" data-testid="welcome-submit">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <PartyPopper className="w-4 h-4" />}
                {busy ? "Setting up…" : "Activate my account"}
              </button>
            </form>

            <p className="text-[11px] text-neutral-500 mt-4 leading-relaxed">
              By activating, you agree to use ReferPro to refer real candidates and follow your team's referral policy.
            </p>

            <Link
              to="/login"
              className="block text-center text-xs font-bold underline text-neutral-700 hover:text-black mt-4"
            >
              ← Back to sign in
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
